import { createI18n } from 'vue-i18n'
import { defineRule, configure } from 'vee-validate'
import { required, email, min } from '@vee-validate/rules'
import { localize, setLocale as setVeeLocale } from '@vee-validate/i18n'
import {
    forceReloadDueToStaleAssets,
    isStaleModuleImportError,
} from '@/utils/stale-client.js'
import { safeLocalStorageGet } from '@/utils/storage.js'

const isBrowser = typeof window !== 'undefined'
const supportedLocales = ['es', 'en', 'pt', 'de', 'nl']

/**
 * Detect the preferred locale for the user.
 * Priority order:
 * 1. Saved preference in localStorage ('preferredLanguage')
 * 2. Browser navigator.languages array
 * 3. Browser navigator.language
 * 4. Fallback locale
 *
 * Normalizes the detected locale to match entries in the supportedLocales list.
 *
 * @param {Object} options
 * @param {string[]} [options.supported=supportedLocales] - Locales that the app can serve.
 * @param {string} [options.fallback='en'] - Fallback locale when detection fails.
 * @param {string} [options.storageKey='preferredLanguage'] - localStorage key to check for saved preference.
 * @returns {string} The detected locale, normalized to a 2-letter code when possible.
 */
export function detectNavigatorLocale({
    supported = supportedLocales,
    fallback = 'en',
    storageKey = 'preferredLanguage',
} = {}) {
    if (!isBrowser) {
        return fallback
    }

    const supportedSet = new Set(
        Array.isArray(supported)
            ? supported.map((localeCode) => localeCode.toLowerCase())
            : []
    )

    // Priority 1: Check localStorage for saved preference
    const savedLanguage = safeLocalStorageGet(storageKey)
    if (
        savedLanguage &&
        typeof savedLanguage === 'string' &&
        (!supportedSet.size || supportedSet.has(savedLanguage.toLowerCase()))
    ) {
        return savedLanguage.toLowerCase()
    }

    // Priority 2: Check navigator.languages array
    const navigatorLanguages = Array.isArray(navigator.languages)
        ? navigator.languages
        : []

    const normalizedNavigatorLanguages = navigatorLanguages
        .map((lang) =>
            typeof lang === 'string' ? lang.slice(0, 2).toLowerCase() : null
        )
        .filter(Boolean)

    for (const lang of normalizedNavigatorLanguages) {
        if (!supportedSet.size || supportedSet.has(lang)) {
            return lang
        }
    }

    // Priority 3: Check if any navigator language includes 'en' as fallback
    if (
        navigatorLanguages.some((lang) =>
            typeof lang === 'string' ? lang.toLowerCase().includes('en') : false
        )
    ) {
        return 'en'
    }

    // Priority 4: Check navigator.language
    const navigatorLanguage =
        typeof navigator.language === 'string'
            ? navigator.language.slice(0, 2).toLowerCase()
            : null

    if (
        navigatorLanguage &&
        (!supportedSet.size || supportedSet.has(navigatorLanguage))
    ) {
        return navigatorLanguage
    }

    // Priority 5: Return fallback
    return fallback
}

const defaultLocale = detectNavigatorLocale({
    supported: supportedLocales,
    fallback: 'en',
})

// Ensure we have a valid locale
const locale = supportedLocales.includes(defaultLocale) ? defaultLocale : 'es'

const messageLoaders = {
    en: () => import('./locales/en.json'),
    es: () => import('./locales/es.json'),
    pt: () => import('./locales/pt.json'),
    de: () => import('./locales/de.json'),
    nl: () => import('./locales/nl.json'),
}

const veeValidateLoaders = {
    en: () => import('@vee-validate/i18n/dist/locale/en.json'),
    es: () => import('@vee-validate/i18n/dist/locale/es.json'),
    pt: () => import('@vee-validate/i18n/dist/locale/pt_BR.json'),
    de: () => import('@vee-validate/i18n/dist/locale/de.json'),
    nl: () => import('@vee-validate/i18n/dist/locale/nl.json'),
}

const loadedMessageLocales = new Set()
const loadedVeeLocales = new Set()
const veeLocaleMessages = {}

async function handleStaleModuleError(error, reason) {
    if (isStaleModuleImportError(error)) {
        await forceReloadDueToStaleAssets({ reason })
    }
}

const i18n = createI18n({
    locale,
    legacy: false,
    fallbackLocale: 'en',
    messages: {},
})

// Vee-validate global rules
defineRule('required', required)
defineRule('email', email)
defineRule('min', min)
defineRule('over18', (value) => {
    const date = new Date(value)
    const now = new Date()
    const diff = now - date
    const age = Math.floor(diff / 31557600000)
    if (age < 18) {
        return 'Debes ser mayor de edad'
    }
    return true
})

// Load locale resources dynamically to reduce initial bundle
async function ensureLocaleMessages(localeToLoad) {
    const loader = messageLoaders[localeToLoad]
    if (loader && !loadedMessageLocales.has(localeToLoad)) {
        try {
            const messagesModule = await loader()
            i18n.global.setLocaleMessage(
                localeToLoad,
                messagesModule.default || messagesModule
            )
            loadedMessageLocales.add(localeToLoad)
        } catch (error) {
            await handleStaleModuleError(
                error,
                `locale-${localeToLoad}-module-missing`
            )
            throw error
        }
    }

    if (!loadedMessageLocales.has('en')) {
        try {
            const fallbackModule = await messageLoaders.en()
            i18n.global.setLocaleMessage(
                'en',
                fallbackModule.default || fallbackModule
            )
            loadedMessageLocales.add('en')
        } catch (error) {
            await handleStaleModuleError(error, 'locale-en-module-missing')
            throw error
        }
    }
}

async function ensureVeeValidateMessages(localeToLoad) {
    const loader = veeValidateLoaders[localeToLoad]
    if (loader && !loadedVeeLocales.has(localeToLoad)) {
        try {
            const errorsModule = await loader()
            veeLocaleMessages[localeToLoad] =
                errorsModule.default || errorsModule
            loadedVeeLocales.add(localeToLoad)
        } catch (error) {
            await handleStaleModuleError(
                error,
                `vee-locale-${localeToLoad}-module-missing`
            )
            throw error
        }
    }

    if (!loadedVeeLocales.has('en')) {
        try {
            const fallbackModule = await veeValidateLoaders.en()
            veeLocaleMessages.en = fallbackModule.default || fallbackModule
            loadedVeeLocales.add('en')
        } catch (error) {
            await handleStaleModuleError(error, 'vee-locale-en-module-missing')
            throw error
        }
    }

    configure({ generateMessage: localize(veeLocaleMessages) })
    setVeeLocale(localeToLoad)
}

export async function loadLocaleResources(targetLocale) {
    const normalizedLocale = supportedLocales.includes(targetLocale)
        ? targetLocale
        : 'en'

    try {
        await ensureLocaleMessages(normalizedLocale)
    } catch (error) {
        console.error('Failed to load app locale messages:', error)
        try {
            await ensureLocaleMessages('en')
            i18n.global.locale.value = 'en'
            return 'en'
        } catch (fallbackError) {
            console.error(
                'Failed to load fallback app locale messages:',
                fallbackError
            )
            throw fallbackError
        }
    }

    try {
        await ensureVeeValidateMessages(normalizedLocale)
    } catch (error) {
        console.error('Failed to load vee-validate locale messages:', error)
        try {
            await ensureVeeValidateMessages('en')
            i18n.global.locale.value = 'en'
            return 'en'
        } catch (fallbackError) {
            console.error(
                'Failed to load fallback vee-validate locale messages:',
                fallbackError
            )
            throw fallbackError
        }
    }

    i18n.global.locale.value = normalizedLocale
    return normalizedLocale
}

export { i18n, locale, supportedLocales }
